import { ReactElement, useEffect, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Select, Checkbox, Typography } from '@weareopenr/component-library';
import { useQuery } from '@tanstack/react-query';
import { useRoles } from 'src/hooks/useRoles/useRoles';
import { getBrands } from 'src/api/getBrands/getBrands';
import { useWizard } from '../../MultistepWizard/WizardContext';
import { IFormValues } from '../types';
import styles from '../UserModal.module.scss';
import { useUserTracking } from 'src/hooks';

interface AccessControlsStepProps {
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
}

export const AccessControlsStep = ({ setErrorMessage }: AccessControlsStepProps): ReactElement => {
  const { data } = useWizard<IFormValues>();
  const updateDataRef = useRef(data.updateData);
  const { trackEvent, captureException } = useUserTracking();

  const {
    control,
    watch,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<Pick<IFormValues, 'role' | 'brands'>>({
    defaultValues: {
      role: data.data.role || '',
      brands: data.data.brands || [],
    },
    mode: 'onChange',
  });

  const role = watch('role');
  const brands = watch('brands') || [];

  // Keep ref updated
  useEffect(() => {
    updateDataRef.current = data.updateData;
  }, [data.updateData]);

  const { roles, isLoading } = useRoles();
  const { data: brandsData } = useQuery({
    queryKey: ['brands'],
    queryFn: getBrands,
    onError: (e: Error) => {
      setErrorMessage(e.message);
      captureException(e);
    },
  });

  // Update wizard data when form fields change
  useEffect(() => {
    if (updateDataRef.current) {
      updateDataRef.current({
        role: role,
        brands: brands,
      });
    }
  }, [role, brands]);

  // Brands validation
  useEffect(() => {
    const isAllBrandsSelected = brands.includes('*');
    const individualBrandsCount = isAllBrandsSelected ? 0 : brands.length;

    if (!isAllBrandsSelected && individualBrandsCount > 10) {
      setError('brands', {
        type: 'manual',
        message: 'Maximum 10 brands can be selected. Select "All Brands" if you need more.',
      });
    } else {
      clearErrors('brands');
    }
  }, [brands, setError, clearErrors]);

  const handleBrandsChange = (newSelectedIds: string[] | ((prev: string[]) => string[])) => {
    let updatedIds: string[];

    if (typeof newSelectedIds === 'function') {
      updatedIds = newSelectedIds(brands);
    } else {
      updatedIds = newSelectedIds;
    }

    const hasAllBrands = updatedIds.includes('*');
    const currentHasAllBrands = brands.includes('*');
    const individualBrandIds = brandOptions.map((brand) => brand.id);
    let finalValue: string[];

    // If "All Brands" was just selected
    if (hasAllBrands && !currentHasAllBrands) {
      finalValue = ['*'];
    }
    // If we're removing "All Brands"
    else if (!hasAllBrands && currentHasAllBrands) {
      if (updatedIds.length > 0) {
        finalValue = updatedIds;
      } else {
        finalValue = updatedIds.filter((id) => id !== '*');
      }
    }
    // If we're adding individual brands and now have all of them
    else if (!hasAllBrands && updatedIds.length === individualBrandIds.length) {
      finalValue = ['*'];
    }
    // Normal case
    else {
      finalValue = updatedIds;
    }
    setValue('brands', finalValue);

    trackEvent('brand_access_changed', {
      brand_count: finalValue.includes('*') ? 'all' : finalValue.length,
    });
  };

  const allBrandsOption = { id: '*', name: 'All Brands' };
  const brandOptions =
    brandsData?.map((brand) => ({
      id: brand.brand_id,
      name: brand.name,
    })) || [];

  const allOptions = [allBrandsOption, ...brandOptions];

  return (
    <div>
      <div className={styles['form-field']}>
        <Controller
          name="role"
          control={control}
          rules={{
            required: 'Please select a role',
          }}
          render={({ field: { ref: _, ...field } }) => (
            <Select
              {...field}
              label="Role"
              options={roles.map((roleOption) => ({
                name: roleOption.name,
                value: roleOption.slug,
              }))}
              error={errors.role?.message}
              loading={isLoading}
              onChange={(value) => {
                field.onChange(value);
                trackEvent('role_selected', { role: value });
              }}
            />
          )}
        />
      </div>
      <div className={styles['form-field']}>
        <div style={{ marginBottom: '12px' }}>
          <Typography size="medium" weight="semi-bold">
            Brands
          </Typography>
        </div>
        <div className={styles['brands-container']}>
          {allOptions.map((option) => (
            <div
              key={option.id}
              className={option.id === '*' ? styles['all-brands-option'] : styles['brand-option']}
            >
              <Checkbox
                label={option.name}
                checked={brands.includes(option.id) || (brands.includes('*') && option.id !== '*')}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const currentBrandSelection = brands.includes('*')
                    ? brandOptions.map((brand) => brand.id)
                    : brands;

                  if (checked) {
                    // Add this option to current selection
                    if (!currentBrandSelection.includes(option.id)) {
                      const newSelection = [...currentBrandSelection, option.id];
                      // Prevent selecting more than 10 individual brands
                      if (
                        option.id !== '*' &&
                        !newSelection.includes('*') &&
                        newSelection.length > 10
                      ) {
                        setError('brands', {
                          type: 'manual',
                          message:
                            'Maximum 10 brands can be selected. Select "All Brands" if you need more.',
                        });
                        return;
                      }
                      handleBrandsChange(newSelection);
                    }
                    return;
                  }

                  // If deselecting "All Brands", deselect everything
                  if (option.id === '*') {
                    handleBrandsChange([]);
                    return;
                  }

                  // Remove this option from current selection
                  handleBrandsChange(currentBrandSelection.filter((id) => id !== option.id));
                }}
              />
            </div>
          ))}
        </div>
        {errors.brands && (
          <Typography size="small" className={styles['error-text']}>
            {errors.brands.message}
          </Typography>
        )}
      </div>
    </div>
  );
};
